import React from 'react';
import { Input } from 'zarm';
import TabInput, { inputProps } from './Input';
import TabContext, { inputOptions } from './context';
import './index.scss';

export type TabulationProps = {
  border?: boolean, // 是否显示边框
  defaultMaxLength?: number, // 允许输入的最大值
  defaultUnavailableText?: string, // 不可输入的字符
  onTabChange?: (
    name: string,
    value: number | string,
    unavailableText: string,
    element?: HTMLInputElement,
  ) => void,
  onInputChange?: (
    name: string,
    value: number | string,
    element?: HTMLInputElement
  ) => void,
  onInputBlur?: (
    name: string,
    value: number | string,
    element?: HTMLInputElement
  ) => void,
}

export type TabulationState = {

}

export default class Tabulation extends React.Component<TabulationProps, TabulationState> {
  static TabInput: React.FC<inputProps> = TabInput;
  static defaultProps: TabulationProps = {
    border: true,
    defaultMaxLength: Infinity,
    onTabChange() { },
    onInputChange() { },
    onInputBlur() { },
  }

  constructor(props: TabulationProps) {
    super(props);
    // 实例集合
    this.inputRefs = [];
  }

  // input实例集合
  inputRefs: Array<HTMLInputElement>
  // 最大索引值
  maxTabIndex: number = 0
  // 当前所在索引
  currentTabIndex: number = 0
  // 当前所有输入框的值是否都为负数
  isAllNegNum: boolean = false
  // 禁止输入的正则替换
  regExpText: RegExp = /0/

  componentDidMount() {
    // 默认第一个获取到焦点
    this.onTabInput(0);
    // 判断索引值除去-1以外的是否存在重复
    const indexArray = this.inputRefs.map((item) => item.tabIndex).filter((tabIndex) => tabIndex !== -1) || [];

    // 去重之后的数组
    const dupliArray = Array.from(new Set(indexArray));

    // 如果两者不想等说明有重复索引值
    if (indexArray.length !== dupliArray.length) {
      throw new Error('There are duplicate index values');
    }
    // 如果是数组首位不是0
    if (indexArray[0] !== 0) {
      throw new Error('tabIndex must start with 0');
    }
    // 如果数组不连续
    let isContinuous = true;
    indexArray.reduce((a, b) => {
      if (Math.abs(a) + 1 !== Math.abs(b)) {
        isContinuous = false;
      }
      return b;
    }, 0);
    if (isContinuous) {
      throw new Error('tabIndex must be continuous');
    }
    // 如果所有的索引值都是负数
    this.isAllNegNum = dupliArray.every(tabIndex => tabIndex < 0);
    // 计算最大索引值
    this.maxTabIndex = Math.max.apply(null, this.inputRefs.map((item) => item.tabIndex));
    // 禁止输入正则
    this.regExpText = new RegExp(`[${this.props.defaultUnavailableText}]`, 'igm');
  }


  /**
   * 选中下一个输入框
   */
  onNext = (event?: any) => {
    event?.preventDefault();
    event?.stopPropagation();
    // 索引加
    let currIndex = Math.abs(this.currentTabIndex) + 1;
    // 如果索引大于了最大索引
    if (currIndex > this.maxTabIndex) {
      currIndex = 0;
    }
    // 切换焦点
    this.onTabInput(currIndex);
    return currIndex;
  }

  /**
   * 选中上一个输入框
   */
  onPrev = () => {
    // 索引减一
    let currIndex = Math.abs(this.currentTabIndex) - 1;
    // 如果索引小于0
    if (currIndex < 0) {
      currIndex = this.maxTabIndex;
    }
    // 切换焦点
    this.onTabInput(currIndex, 'prev');
  }

  /**
   * 切换焦点
   * @param tabIndex tab索引
   * @param event 事件对象
   */
  onTabInput = (tabIndex: number, direction: string = 'next'): void => {

    const inputRef = this.inputRefs.find(item => item.tabIndex === tabIndex);

    // tabIndex对应的input.tabIndex可能是负数
    // 说明它的下一个是要跳过的的索引
    // 此时继续找寻下一个，不是负数的输入框
    // 如果所应的输入框都是负数，那么就从会出现死循环一直寻找下一个tabIndex不为负数的索引
    // 因此此处判断只有输入框的tabIndex不全为负数时才寻找下一个
    if (!inputRef && !this.isAllNegNum) {
      // 判断索引加减
      direction === 'next' ? ++tabIndex : --tabIndex;
      // 设置当前所应
      this.setCurrentIndex(tabIndex);
      // 再次切换
      this.onTabInput(tabIndex, direction);
      return;
    }

    // 如果能够找到对应索引的输入框
    if (inputRef) {
      // 获取焦点
      inputRef.focus();
      inputRef.click();
      // setTimeout(() => {
      //   // 全选
      //   // inputRef.select();
      // }, 1);
    }
  }

  /**
   * 设置当前输入框索引
   * @param tabIndex 
   */
  setCurrentIndex(tabIndex: number): void {
    // 设置索引
    this.currentTabIndex = tabIndex;
  }

  /**
   * 通过字段名称设置值
   * @param name 字段名称
   */
  setValueByName(name: string, value: number | string): void {
    const inputElement = this.inputRefs.find((item) => item.name === name);
    // 设置值
    if (inputElement) {
      inputElement.value = value + '';
    }
  }

  /**
   * 通过tabIndex称设置值
   * @param tabIndex tabIndex
   */
  setValueByTabIndex(tabIndex: number, value: number | string): void {
    const inputElement = this.inputRefs.find((item) => item.tabIndex === tabIndex);
    // 设置值
    if (inputElement) {
      inputElement.value = value + '';
    }
  }

  /**
   * 创建组件实例引用
   * @param ref 
   * @param tabIndex 
   */
  createRef = (ref: HTMLInputElement, tabIndex: number): void => {
    this.inputRefs.push(ref);
  }

  /**
   * input输入框获取焦点
   * @param options.name 元素字段名称
   * @param options.value 输入框的值
   * @param options.unavailableText 输入框禁止输入的值
   * @param options.element input元素
   */
  onFocus = (options: inputOptions): void => {
    const { name, value, unavailableText, element } = options;
    const tabIndex = element?.tabIndex || 0;
    // 如果是同一个元素获取焦点直接返回不重复粗发onTabChange
    // if (this.currentTabIndex === tabIndex) return;
    // 设置索引
    this.setCurrentIndex(tabIndex);
    // 触发外部onFcous
    if (this.props.onTabChange) {
      this.props.onTabChange(name, value, unavailableText || '', element);
    }
  }

  /**
   * input输入框获取焦点
   * @param options.name 元素字段名称
   * @param options.value 输入框的值
   * @param options.element input元素
   */
  onBlur = (options: inputOptions): void => {
    const { name, value, element } = options;
    if (this.props.onInputBlur) {
      this.props.onInputBlur(name, value, element);
    }
  }
  /**
   * 点击输入框
   * @param name 字段
   */
  onClick = (element: any) => {
    // 全选
    element?.select();
  }

  /**
   * 退格删除
   */
  onBackspace = (event: any) => {
    event?.preventDefault();
    event?.stopPropagation();
    // 获取最后一次获取焦点的元素
    const inputRef = this.inputRefs.find((item) => item.tabIndex === this.currentTabIndex);
    if (inputRef) {
      // input的name值
      const name = inputRef.name;
      // 重新获取焦点
      inputRef.focus();
      // 获取输入框的值
      let value: any = inputRef.value;
      // 获取光标开始与结束位置
      const { selectionStart: start, selectionEnd: end } = inputRef;
      // 如果开始与位置与结束位置相等 && 都为 0，说明光标在输入框最前部
      // 此时应该跳到上一个输入框
      // 如果tabIndex是0就不跳
      if (start === end && start === 0 && inputRef.tabIndex !== 0) {
        this.onPrev();
        // 如果不在首位且没有选中多个的范围那么就删除对应的字符
      } else {
        // 删除所有
        // 回调外部值修改
        if (this.props.onInputChange) {
          this.props.onInputChange(name, '', inputRef);
        }
      }
      // else if (end && start === end && start !== 0) {
      //   value = value.split('');
      //   // 删除的起始位置
      //   const startIndex = end - 1;
      //   // 删除选区字符串
      //   value.splice(startIndex, 1);
      //   // 拼接
      //   value = value.join('');
      //   // 设置输入框的值
      //   inputRef.value = value;
      //   // 设置光标位置
      //   inputRef.selectionStart = startIndex;
      //   inputRef.selectionEnd = startIndex;
      //   // 回调外部值修改
      //   if (this.props.onInputChange) {
      //     this.props.onInputChange(name, value, inputRef);
      //   }
      // } else if (end && (start || start === 0)) { // 删除范围
      //   value = value.split('');
      //   // 删除选区字符串
      //   value.splice(start, end - start);
      //   // 拼接
      //   value = value.join('');
      //   // 设置输入框的值
      //   inputRef.value = value;
      //   // 设置光标位置
      //   inputRef.selectionStart = start;
      //   inputRef.selectionEnd = start;
      //   // 回调外部值修改
      //   if (this.props.onInputChange) {
      //     this.props.onInputChange(name, value, inputRef);
      //   }
      // }
    }
  }

  /**
   * 键盘输入改变值
   * @param value 输入的键盘值
   */
  inputKeyValueChange = (value: string) => {

    const { defaultUnavailableText } = this.props;
    const inputElement = this.inputRefs.find((item) => item.tabIndex === this.currentTabIndex);
    if (inputElement) {
      value = value.replace(this.regExpText, '');
      // 首先获取焦点
      // inputElement.focus();
      // 获取光标开始与结束位置
      // inputElement.selectionStart = inputElement?.value.length;
      const { selectionStart: start, selectionEnd: end } = inputElement;
      if (start === null || end === null) return;
      // 此时文本的长度
      const length = inputElement!.value?.length;
      // 跳到下一个
      const maxLength = inputElement?.maxLength || Infinity;
      
      if (length >= maxLength && (end - start !== length)) {
        // this.onNext();
        inputElement.value = '';
         // 回调外部值修改
         if (this.props.onInputChange) {
          this.props.onInputChange(inputElement.name, '', inputElement);
        }
      }
      // 在光标处插入值
      // 在贯标指定位置前面插入值
      const valueArrary = inputElement.value.split('');
      // 如果是输入的是特殊值需要插入到光标前部
      if (['+', '-'].indexOf(value) > -1) {
        // 插入到前面
        valueArrary.unshift(value);
      } else {
        // 替换位置
        valueArrary.splice(start, end - start, value);
      }
      // 设置值
      inputElement.value = valueArrary.join('');
      // 设置光标位置
      inputElement.selectionStart = start + 1;
      inputElement.selectionEnd = start + 1;
      // 调用外部值改变
      if (this.props.onInputChange) {
        this.props.onInputChange(inputElement.name, inputElement.value, inputElement);
      }
      if (inputElement!.value?.length >= maxLength) {
        // 截取限制范围内的长度
        inputElement.value = inputElement.value.substring(0, maxLength);
        // 切换下一个
        const currentIndex = this.onNext();
        return currentIndex;
      }
    }
  }

  /**
   * 通过名称自动获取焦点
   * @param name input.name
   */
  inputAutoFocusByName(name: string) {
    const inputElement = this.inputRefs.find((item) => item.name === name);
    if (inputElement) {
      inputElement.focus();
      // inputElement.select();
      inputElement.selectionStart = 0;
      inputElement.selectionEnd = inputElement!.value?.length;
    }
  }

  /**
   * 通过索引自动获取焦点
   * @param tabIndex input.tabIndex
   */
  inputAutoFocusByTabIndex(tabIndex: number) {
    const inputElement = this.inputRefs.find((item) => item.tabIndex === tabIndex);
    if (inputElement) {
      inputElement.focus();
      // inputElement.select();
      inputElement.selectionStart = 0;
      inputElement.selectionEnd = inputElement!.value?.length;
    }
  }

  render() {
    const { defaultMaxLength, defaultUnavailableText, border } = this.props;

    return (
      <TabContext.Provider value={{
        createRef: this.createRef,
        onFocus: this.onFocus,
        onBlur: this.onBlur,
        onClick: this.onClick,
        // onChange: this.onChange,
        onNext: this.onNext,
        maxLength: defaultMaxLength,
        unavailableText: defaultUnavailableText,
        border,
      }}>
        {this.props.children}
      </TabContext.Provider>
    )
  }
}
